import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { collection, getDocs, query, where, orderBy, doc, setDoc, addDoc, updateDoc, deleteDoc, Timestamp, onSnapshot } from 'firebase/firestore';
import { ref, getDownloadURL, listAll } from 'firebase/storage';
import { db, storage } from '../config/firebase';
import { useAuth } from './AuthContext';
import {
  parseBankCharges,
  parseCards,
} from '../utils/xlsParser';
import {
  getWOsForUnit as getWOsForUnitQuery,
  getWOsForProperty as getWOsForPropertyQuery,
  getLaborForWO as getLaborForWOQuery,
  getPartsForWO as getPartsForWOQuery,
  getOpenWOsForTech as getOpenWOsForTechQuery,
  getCardsForTech as getCardsForTechQuery,
  getCardsForUser as getCardsForUserQuery,
  getAvailableCards as getAvailableCardsQuery,
  getCurrentTech as getCurrentTechQuery,
  getRecentLaborWOsForTech as getRecentLaborWOsForTechQuery,
  computeLeaderboardData as computeLeaderboardDataQuery,
} from '../utils/dataQueries';
import {
  getNotesForUnit as getNotesForUnitQuery,
  getNotesForProperty as getNotesForPropertyQuery,
  getRelevantNotesForBriefing as getRelevantNotesForBriefingQuery,
  searchNotes as searchNotesQuery,
  formatNotesForPrompt,
  validateNote,
  getPropertyKnowledgeStats as getPropertyKnowledgeStatsQuery,
} from '../utils/knowledgeEngine';
import {
  predictPartsForWO as predictPartsForWOQuery,
  formatPartsListForPrompt,
  classifyIssueType,
} from '../utils/partsIntelligence';
import {
  detectRecurringIssues as detectRecurringIssuesQuery,
  findCrossPropertyPatterns as findCrossPropertyPatternsQuery,
  calculateFailureProbability as calculateFailureProbabilityQuery,
  getPropertyHealthScore as getPropertyHealthScoreQuery,
  analyzeSeasonalPatterns as analyzeSeasonalPatternsQuery,
} from '../utils/predictiveEngine';
import {
  scoreRepairConfidence as scoreRepairConfidenceQuery,
  getTechReliabilityScore as getTechReliabilityScoreQuery,
  getUnitRepairSummary as getUnitRepairSummaryQuery,
} from '../utils/repairConfidence';
// WO review imports disabled — flagged WO workflow paused
// import { getWOsNeedingReview, buildReviewDocument } from '../utils/woReviewEngine';
// import { analyzeWOEfficiency } from '../utils/openai';
import {
  isCompletedWO,
  isCanceledWO,
  normalizeFirestoreWO,
  normalizeFirestoreLabor,
  normalizeFirestoreSurvey,
  normalizeFirestoreProperty,
  normalizeFirestoreUnit,
  recentWindowCutoff,
} from '../utils/dataQueries';
import { diffUnitDetails } from '../utils/unitDetailsHelpers';

// Data source: 'firestore' or 'files'
// Set to 'firestore' after running: npm run import-data
const DATA_SOURCE = 'firestore';

// Data file paths (in public folder for local dev, Firebase Storage for production)
// Use import.meta.env.BASE_URL to handle /tradeskit/ base path
const BASE = import.meta.env.BASE_URL || '/';
// Only non-synced data files remain (bank_charges, cards are not from AppFolio sync)
const DATA_FILES = {
  bankCharges: `${BASE}data/bank_charges.csv`,
  cards: `${BASE}data/cards.csv`,
};

const DataContext = createContext(null);

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

export const DataProvider = ({ children }) => {
  const { user, userProfile, canWrite } = useAuth();

  // All users from Firestore (for admin views)
  const [users, setUsers] = useState([]);

  // Helper to guard write operations - throws if demo user
  const requireWriteAccess = useCallback(() => {
    if (!canWrite || !canWrite()) {
      throw new Error('Demo users cannot modify data');
    }
  }, [canWrite]);

  // Data state
  const [workOrders, setWorkOrders] = useState([]);
  const [laborEntries, setLaborEntries] = useState([]);
  const [units, setUnits] = useState([]);
  const [properties, setProperties] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [bankCharges, setBankCharges] = useState([]);
  const [satisfactionScores, setSatisfactionScores] = useState([]);
  const [partsUsage, setPartsUsage] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [lockChanges, setLockChanges] = useState([]);
  const [inspections, setInspections] = useState([]);
  const [consultSessions, setConsultSessions] = useState([]);
  const [cards, setCards] = useState([]);
  const [lockCodes, setLockCodes] = useState([]);
  const [unitDetails, setUnitDetails] = useState([]);
  const [unitDetailChanges, setUnitDetailChanges] = useState([]);
  const [insuranceEstimates, setInsuranceEstimates] = useState([]);
  const [knowledgeNotes, setKnowledgeNotes] = useState([]);
  const [woReviews, setWoReviews] = useState([]);
  const [syncStatus, setSyncStatus] = useState(null);

  // Admin POV (Point of View) - allows admins to "view as" a specific technician
  // Persisted to localStorage so it survives navigation between /admin and /trades-app
  const [povTechId, setPovTechId] = useState(() => {
    try {
      return localStorage.getItem('admin_pov_tech_id') || null;
    } catch {
      return null;
    }
  });

  // Loading and refresh state
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [error, setError] = useState(null);

  // Fetch and parse a single XLS file
  const fetchAndParseXLS = async (url, parser) => {
    try {
      console.log(`Fetching: ${url}`);
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch ${url}: ${response.status}`);
      }
      const arrayBuffer = await response.arrayBuffer();
      console.log(`Parsing ${url} (${arrayBuffer.byteLength} bytes)`);
      const result = await parser(arrayBuffer);
      console.log(`Parsed ${url}: ${result.length} records`);
      return result;
    } catch (err) {
      console.error(`Error loading ${url}:`, err);
      return [];
    }
  };

  // Load data from Firestore collections (raw AppFolio format → normalized)
  const loadFromFirestore = useCallback(async () => {
    console.log('Loading data from Firestore...');

    try {
      // work_orders and labor_entries grow forever; the other collections are
      // bounded by physical inventory/staff. So we two-phase the unbounded two:
      // Phase 1 loads the last 30 days for fast initial paint, then a background
      // read (below) backfills full history for all-time analytics.
      const mapWOs = (snap) => snap.docs.map(d => {
        const raw = { firestoreId: d.id, ...d.data() };
        // AppFolio field names (work_order_id) → normalize; else legacy app schema
        return raw.work_order_id ? normalizeFirestoreWO(raw) : { id: d.id, ...d.data() };
      });
      const mapLabor = (snap) => snap.docs.map(d => {
        const raw = { firestoreId: d.id, ...d.data() };
        return raw.labor_detail_id ? normalizeFirestoreLabor(raw) : { id: d.id, ...d.data() };
      });

      // Date-only cutoff sorts correctly against both YYYY-MM-DD and ISO datetime strings.
      const cutoff = recentWindowCutoff(30);

      const [
        workOrdersSnap,
        laborSnap,
        propertiesSnap,
        unitsSnap,
        techniciansSnap,
        bankChargesSnap,
        surveysSnap,
        inventorySnap,
        cardsSnap,
        lockCodesSnap,
        syncMetaSnap,
      ] = await Promise.all([
        getDocs(query(collection(db, 'work_orders'), where('created_at', '>=', cutoff))),
        getDocs(query(collection(db, 'labor_entries'), where('date', '>=', cutoff))),
        getDocs(collection(db, 'properties')),
        getDocs(collection(db, 'units')),
        getDocs(collection(db, 'technicians')),
        getDocs(collection(db, 'bank_charges')),
        getDocs(collection(db, 'surveys')),
        getDocs(collection(db, 'inventory')),
        getDocs(collection(db, 'cards')),
        getDocs(collection(db, 'lock_codes')),
        getDocs(collection(db, 'sync_metadata')),
      ]);

      // Normalize raw AppFolio data from Firestore
      const wos = mapWOs(workOrdersSnap);
      const labor = mapLabor(laborSnap);

      const props = propertiesSnap.docs.map(d => {
        const raw = { firestoreId: d.id, ...d.data() };
        return raw.property_id ? normalizeFirestoreProperty(raw) : { id: d.id, ...d.data() };
      });

      const unitData = unitsSnap.docs.map(d => {
        const raw = { firestoreId: d.id, ...d.data() };
        return raw.unit_id ? normalizeFirestoreUnit(raw) : { id: d.id, ...d.data() };
      });

      const techs = techniciansSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const charges = bankChargesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const inventoryData = inventorySnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const cardData = cardsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const lockCodeData = lockCodesSnap.docs.map(d => ({ id: d.id, ...d.data() }));

      // Normalize surveys → satisfaction scores
      const surveyData = surveysSnap.docs.map(d => {
        const raw = { firestoreId: d.id, ...d.data() };
        if (raw.survey_id) return normalizeFirestoreSurvey(raw);
        // Legacy format
        return {
          id: d.id,
          techName: raw.tech,
          score: raw.satisfaction,
          woId: raw.woId,
          date: raw.dateReceived || raw.date_received || '',
          feedback: raw.feedback,
        };
      });

      // Load sync metadata
      const syncMetaDoc = syncMetaSnap.docs.find(d => d.id === 'state');
      if (syncMetaDoc) {
        setSyncStatus(syncMetaDoc.data());
      }

      console.log(`Loaded from Firestore (recent 30d): ${wos.length} WOs, ${labor.length} labor, ${props.length} properties (${props.filter(p => p.lat).length} geocoded), ${cardData.length} cards, ${lockCodeData.length} lock codes`);

      setWorkOrders(wos);
      setLaborEntries(labor);
      setProperties(props);
      setUnits(unitData);
      setTechnicians(techs);
      setBankCharges(charges);
      setSatisfactionScores(surveyData);
      setPartsUsage(inventoryData);
      setCards(cardData);
      setLockCodes(lockCodeData);
      setLastRefresh(new Date());

      // Phase 2: backfill full history in the background (not awaited) so all-time
      // analytics (seasonal patterns, unit repair history) get complete data. Each
      // read replaces the recent slice with the full superset once it lands.
      getDocs(collection(db, 'work_orders'))
        .then(snap => { setWorkOrders(mapWOs(snap)); console.log(`Backfilled ${snap.size} WOs (full history)`); })
        .catch(err => console.error('WO history backfill failed:', err));
      getDocs(collection(db, 'labor_entries'))
        .then(snap => { setLaborEntries(mapLabor(snap)); console.log(`Backfilled ${snap.size} labor (full history)`); })
        .catch(err => console.error('Labor history backfill failed:', err));

      return true;
    } catch (err) {
      console.error('Error loading from Firestore:', err);
      setError(err.message);
      return false;
    }
  }, []);

  // Load data from local CSV files (legacy fallback — only non-synced data)
  const loadXLSData = useCallback(async () => {
    console.log('Loading data from local CSV files (legacy fallback)...');

    try {
      const [charges, cardData] = await Promise.all([
        fetchAndParseXLS(DATA_FILES.bankCharges, parseBankCharges),
        fetchAndParseXLS(DATA_FILES.cards, parseCards),
      ]);

      console.log(`Loaded: ${charges.length} bank charges, ${cardData.length} cards`);

      setBankCharges(charges);
      setCards(cardData);

      setLastRefresh(new Date());
      return true;
    } catch (err) {
      console.error('Error loading CSV data:', err);
      setError(err.message);
      return false;
    }
  }, []);

  // Load Firestore collections (receipts, lock_changes, inspections, consult_sessions, users)
  const loadFirestoreData = useCallback(async () => {
    if (!user) return;

    // Load a collection to plain docs; failure of one collection → [] (independent, parallel)
    const load = (name) =>
      getDocs(collection(db, name))
        .then(snap => snap.docs.map(doc => ({ id: doc.id, ...doc.data() })))
        .catch(err => { console.error(`Error loading ${name}:`, err); return []; });

    const [
      receipts,
      lockChanges,
      inspections,
      consultSessions,
      usersData,
      insuranceEstimates,
      knowledgeNotes,
      unitDetailsData,
      unitDetailChanges,
    ] = await Promise.all([
      load('receipts'),
      load('lock_changes'),
      load('inspections'),
      load('consult_sessions'),
      load('users'),
      load('insurance_estimates'),
      load('knowledge_notes'),
      load('unit_details'),
      load('unit_detail_changes'),
    ]);

    setReceipts(receipts);
    setLockChanges(lockChanges);
    setInspections(inspections);
    setConsultSessions(consultSessions);
    setUsers(usersData);
    // Sort client-side to avoid index requirement
    insuranceEstimates.sort((a, b) => (b.updatedAt?.seconds || 0) - (a.updatedAt?.seconds || 0));
    setInsuranceEstimates(insuranceEstimates);
    setKnowledgeNotes(knowledgeNotes);
    setUnitDetails(unitDetailsData);
    setUnitDetailChanges(unitDetailChanges);
  }, [user]);

  // Refresh data — reload from Firestore (synced by Cloud Function)
  const refreshDataFromStorage = useCallback(async () => {
    setRefreshing(true);
    setError(null);

    try {
      if (DATA_SOURCE === 'firestore') {
        await loadFromFirestore();
      } else {
        await loadXLSData();
      }
      await loadFirestoreData();
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Error refreshing data:', err);
      setError(err.message);
    } finally {
      setRefreshing(false);
    }
  }, [loadXLSData, loadFromFirestore, loadFirestoreData]);

  // Initial data load
  useEffect(() => {
    const initData = async () => {
      setLoading(true);

      // Load all data from Firestore (synced by Cloud Function)
      if (DATA_SOURCE === 'firestore') {
        await loadFromFirestore();
      } else {
        await loadXLSData();
      }

      // Always load user-generated data from Firestore
      await loadFirestoreData();
      setLoading(false);
    };

    if (user) {
      initData();
    } else {
      setLoading(false);
    }
  }, [user, loadXLSData, loadFromFirestore, loadFirestoreData]);

  // Helper functions for data access

  // Admin POV override - when set, getCurrentTech returns this tech instead
  // Persisted to localStorage so it works across /admin and /trades-app
  const setPovTech = useCallback((techId) => {
    if (techId) {
      console.log(`[POV] Admin viewing as tech: ${techId}`);
      try {
        localStorage.setItem('admin_pov_tech_id', techId);
      } catch (e) {
        console.warn('[POV] Could not persist to localStorage:', e);
      }
    } else {
      console.log('[POV] Cleared - returning to normal view');
      try {
        localStorage.removeItem('admin_pov_tech_id');
      } catch (e) {
        console.warn('[POV] Could not clear localStorage:', e);
      }
    }
    setPovTechId(techId);
  }, []);

  const getCurrentTech = useCallback(() => {
    return getCurrentTechQuery(technicians, user, povTechId);
  }, [user, technicians, povTechId]);

  const getOpenWOsForTech = useCallback((techName) => {
    const tech = techName || getCurrentTech()?.name;
    return getOpenWOsForTechQuery(workOrders, tech);
  }, [workOrders, getCurrentTech]);

  const getWOsForUnit = useCallback((unitId, limit = 20) => {
    return getWOsForUnitQuery(workOrders, unitId, limit);
  }, [workOrders]);

  const getWOsForProperty = useCallback((propertyId, excludeUnitId = null, limit = 50) => {
    return getWOsForPropertyQuery(workOrders, propertyId, excludeUnitId, limit);
  }, [workOrders]);

  const getLaborForWO = useCallback((woId) => {
    return getLaborForWOQuery(laborEntries, woId);
  }, [laborEntries]);

  const getPartsForWO = useCallback((woId) => {
    return getPartsForWOQuery(partsUsage, woId);
  }, [partsUsage]);

  // TODO: FUTURE - Fuzzy Match Review System
  // Problem: Names vary across data sources (e.g., "Bijan" vs "BIJAN DoyLe" vs "Bijan Doyle",
  //          "Zay" vs "Xavier Aries"). Current fuzzy matching may produce incorrect links.
  // Solution:
  //   1. Create Firestore collection `verified_matches` with schema:
  //      { sourceType: 'tech_to_card', sourceId: 'bijan-doyle', targetId: 'card-2329',
  //        confidence: 0.85, verifiedBy: null, verifiedAt: null, status: 'pending' }
  //   2. When fuzzy matching, check verified_matches first for confirmed links
  //   3. If no verified match, perform fuzzy match and log to `pending_matches` for review
  //   4. Admin UI to review pending matches: show source, target, confidence score
  //   5. On approval, move to verified_matches with verifiedBy/verifiedAt
  //   6. On rejection, add to `rejected_matches` to avoid re-suggesting
  // Match types needed: tech_to_card, tech_name_variants, property_name_variants

  // Get card(s) for a tech (by name match) - LEGACY fuzzy matching
  const getCardsForTech = useCallback((techName) => {
    return getCardsForTechQuery(cards, techName);
  }, [cards]);

  // Get cards for a user - explicit assignments first, then inference fallback
  // This is the NEW preferred method for card matching
  const getCardsForUser = useCallback((userId = null) => {
    // If no userId provided, use current user
    const targetUser = userId
      ? users.find(u => u.uid === userId || u.id === userId)
      : userProfile;

    return getCardsForUserQuery(cards, users, targetUser);
  }, [cards, users, userProfile]);

  // Get cards that are available (not assigned to other users)
  const getAvailableCards = useCallback(() => {
    return getAvailableCardsQuery(cards, users);
  }, [cards, users]);

  // Assign a card to the current user (self-service)
  const assignCardToSelf = useCallback(async (cardLast4) => {
    requireWriteAccess();
    if (!userProfile) throw new Error('Not authenticated');

    const currentAssigned = userProfile.assignedCards || [];
    if (currentAssigned.includes(cardLast4)) {
      return; // Already assigned
    }

    // Check if card is already assigned to someone else
    const otherUser = users.find(u =>
      u.uid !== userProfile.uid &&
      (u.assignedCards || []).includes(cardLast4)
    );
    if (otherUser) {
      throw new Error(`Card is already assigned to ${otherUser.displayName || otherUser.email}`);
    }

    // Update user's assignedCards
    const newAssigned = [...currentAssigned, cardLast4];
    await updateDoc(doc(db, 'users', userProfile.uid), {
      assignedCards: newAssigned,
      updatedAt: Timestamp.now(),
    });

    // Update local users state
    setUsers(prev => prev.map(u =>
      u.uid === userProfile.uid
        ? { ...u, assignedCards: newAssigned }
        : u
    ));
  }, [userProfile, users]);

  // Unassign a card from the current user
  const unassignCardFromSelf = useCallback(async (cardLast4) => {
    requireWriteAccess();
    if (!userProfile) throw new Error('Not authenticated');

    const currentAssigned = userProfile.assignedCards || [];
    if (!currentAssigned.includes(cardLast4)) {
      return; // Not assigned
    }

    const newAssigned = currentAssigned.filter(c => c !== cardLast4);
    await updateDoc(doc(db, 'users', userProfile.uid), {
      assignedCards: newAssigned,
      updatedAt: Timestamp.now(),
    });

    // Update local users state
    setUsers(prev => prev.map(u =>
      u.uid === userProfile.uid
        ? { ...u, assignedCards: newAssigned }
        : u
    ));
  }, [userProfile]);

  // Get unmatched bank charges, optionally filtered by tech
  const getUnmatchedBankCharges = useCallback((forCurrentTech = true) => {
    const matchedChargeIds = receipts.map(r => r.bankChargeId);
    let charges = bankCharges.filter(charge => !matchedChargeIds.includes(charge.id));

    // Filter by current tech's card(s) if requested
    if (forCurrentTech) {
      const tech = getCurrentTech();
      if (tech) {
        const techCards = getCardsForTech(tech.name);
        const techCardNumbers = techCards.map(c => c.cardLast4);
        if (techCardNumbers.length > 0) {
          charges = charges.filter(charge => techCardNumbers.includes(charge.cardLast4));
        }
      }
    }

    return charges;
  }, [bankCharges, receipts, getCurrentTech, getCardsForTech]);

  const getReceiptsForWO = useCallback((woId) => {
    return receipts.filter(r => r.woId === woId);
  }, [receipts]);

  const getUnitLockInfo = useCallback((unitId) => {
    return units.find(u => u.id === unitId || u.unitId === unitId);
  }, [units]);

  const getPropertyInfo = useCallback((propertyId) => {
    return properties.find(p => p.id === propertyId);
  }, [properties]);

  // Get tech name from card last 4 digits
  const getTechByCard = useCallback((cardLast4) => {
    const card = cards.find(c => c.cardLast4 === cardLast4);
    return card ? card.holderName || card.cardName : null;
  }, [cards]);

  // Get lock code info for a unit
  const getLockCode = useCallback((unitId) => {
    return lockCodes.find(lc => String(lc.unitId) === String(unitId));
  }, [lockCodes]);

  // Get unit details (HVAC, locks, filter sizes)
  const getUnitDetails = useCallback((unitId) => {
    return unitDetails.find(ud => String(ud.unitId) === String(unitId)) || null;
  }, [unitDetails]);

  // Get changelog for a unit
  const getUnitDetailChanges = useCallback((unitId) => {
    return unitDetailChanges.filter(c => String(c.unitId) === String(unitId));
  }, [unitDetailChanges]);

  // Save unit details — diffs, writes changelog, updates Firestore + local state
  const saveUnitDetails = useCallback(async (unitId, newValues) => {
    requireWriteAccess();
    const current = unitDetails.find(ud => String(ud.unitId) === String(unitId)) || {};
    const changes = diffUnitDetails(current, newValues);

    if (changes.length === 0) return null; // Nothing changed

    const now = Timestamp.now();
    // Author is ALWAYS the authenticated user who performed the write — never
    // getCurrentTech() (a view/POV concept whose fallback would stamp an admin's
    // edit with a real tech's name). updatedBy stays the auth uid below.
    const userName = userProfile?.displayName || user?.displayName || user?.email || 'Unknown';

    // 1. Write/update unit_details doc
    const detailDoc = {
      unitId: String(unitId),
      ...newValues,
      updatedAt: now,
      updatedBy: user?.uid,
      updatedByName: userName,
    };
    // Set createdAt only if new
    if (!current.unitId) {
      detailDoc.createdAt = now;
    }

    await setDoc(doc(db, 'unit_details', String(unitId)), detailDoc, { merge: true });

    // 2. Write changelog entry
    const changeDoc = {
      unitId: String(unitId),
      changes,
      changedBy: user?.uid,
      changedByName: userName,
      changedByEmail: user?.email,
      createdAt: now,
    };
    const changeRef = await addDoc(collection(db, 'unit_detail_changes'), changeDoc);

    // 3. Dual-write lock fields to lock_codes for backward compat
    const lockFieldChanged = changes.some(c =>
      ['unitLockCode', 'commonLockCode'].includes(c.field)
    );
    if (lockFieldChanged) {
      const lockCodeDocId = `lock-${unitId}`;
      const lockUpdate = {
        unitId: String(unitId),
        unitLockCode: newValues.unitLockCode || current.unitLockCode || '',
        commonLockCode: newValues.commonLockCode || current.commonLockCode || '',
        lastChangedBy: userName,
        lastChangedAt: new Date().toISOString(),
      };
      await setDoc(doc(db, 'lock_codes', lockCodeDocId), lockUpdate, { merge: true });

      // Update local lock_codes state
      setLockCodes(prev => {
        const existing = prev.find(lc => String(lc.unitId) === String(unitId));
        if (existing) {
          return prev.map(lc =>
            String(lc.unitId) === String(unitId)
              ? { ...lc, ...lockUpdate, id: lockCodeDocId }
              : lc
          );
        }
        return [...prev, { ...lockUpdate, id: lockCodeDocId }];
      });
    }

    // 4. Update local state
    setUnitDetails(prev => {
      const existing = prev.find(ud => String(ud.unitId) === String(unitId));
      if (existing) {
        return prev.map(ud =>
          String(ud.unitId) === String(unitId)
            ? { ...ud, ...detailDoc, id: String(unitId) }
            : ud
        );
      }
      return [...prev, { ...detailDoc, id: String(unitId) }];
    });

    setUnitDetailChanges(prev => [...prev, { id: changeRef.id, ...changeDoc }]);

    return changeRef.id;
  }, [user, userProfile, unitDetails]);

  // Add new receipt
  const addReceipt = useCallback(async (receiptData) => {
    requireWriteAccess();
    try {
      const docRef = await addDoc(collection(db, 'receipts'), {
        ...receiptData,
        createdAt: Timestamp.now(),
        techId: user?.uid,
        techEmail: user?.email,
      });

      setReceipts(prev => [...prev, { id: docRef.id, ...receiptData }]);
      return docRef.id;
    } catch (err) {
      console.error('Error adding receipt:', err);
      throw err;
    }
  }, [user]);

  // Add lock change - saves to history AND updates lock_codes with new values
  const addLockChange = useCallback(async (lockChangeData) => {
    requireWriteAccess();
    try {
      // Author = the authenticated user, not getCurrentTech() (avoids stamping
      // an admin's change with the fallback tech's name).
      const author = userProfile?.displayName || user?.displayName || user?.email || 'Unknown';
      // 1. Save to lock_changes collection (history log)
      const docRef = await addDoc(collection(db, 'lock_changes'), {
        ...lockChangeData,
        createdAt: Timestamp.now(),
        techId: user?.uid,
        techEmail: user?.email,
        techName: author,
      });

      setLockChanges(prev => [...prev, { id: docRef.id, ...lockChangeData }]);

      // 2. Update lock_codes collection with new values
      const lockCodeDocId = `lock-${lockChangeData.unitId}`;
      const existingLockCode = lockCodes.find(lc => String(lc.unitId) === String(lockChangeData.unitId));

      const updatedLockCode = {
        unitId: String(lockChangeData.unitId),
        propertyId: existingLockCode?.propertyId || '',
        property: lockChangeData.property || existingLockCode?.property || '',
        unitAddress: existingLockCode?.unitAddress || '',
        // New values become current
        unitLockCode: lockChangeData.newLock || existingLockCode?.unitLockCode || '',
        commonLockCode: lockChangeData.newCommon || existingLockCode?.commonLockCode || '',
        // Old values stored as previous
        prevUnitLockCode: lockChangeData.previousLock || existingLockCode?.unitLockCode || '',
        prevCommonLockCode: lockChangeData.previousCommon || existingLockCode?.commonLockCode || '',
        lastChangedBy: author,
        lastChangedAt: new Date().toISOString(),
        lockNotes: existingLockCode?.lockNotes || '',
        lockboxCode: existingLockCode?.lockboxCode || '',
      };

      await setDoc(doc(db, 'lock_codes', lockCodeDocId), updatedLockCode, { merge: true });

      // 3. Update local state
      setLockCodes(prev => prev.map(lc =>
        String(lc.unitId) === String(lockChangeData.unitId)
          ? { ...lc, ...updatedLockCode, id: lockCodeDocId }
          : lc
      ));

      return docRef.id;
    } catch (err) {
      console.error('Error adding lock change:', err);
      throw err;
    }
  }, [user, userProfile, lockCodes]);

  // Add inspection
  const addInspection = useCallback(async (inspectionData) => {
    requireWriteAccess();
    try {
      const docRef = await addDoc(collection(db, 'inspections'), {
        ...inspectionData,
        createdAt: Timestamp.now(),
        techId: user?.uid,
        techEmail: user?.email,
      });

      setInspections(prev => [...prev, { id: docRef.id, ...inspectionData }]);
      return docRef.id;
    } catch (err) {
      console.error('Error adding inspection:', err);
      throw err;
    }
  }, [user]);

  // Update inspection
  const updateInspection = useCallback(async (inspectionId, updates) => {
    requireWriteAccess();
    try {
      await setDoc(doc(db, 'inspections', inspectionId), updates, { merge: true });
      setInspections(prev => prev.map(i =>
        i.id === inspectionId ? { ...i, ...updates } : i
      ));
    } catch (err) {
      console.error('Error updating inspection:', err);
      throw err;
    }
  }, []);

  // Add consult session
  const addConsultSession = useCallback(async (sessionData) => {
    requireWriteAccess();
    try {
      const now = Timestamp.now();
      const docData = {
        ...sessionData,
        createdAt: now,
        techId: user?.uid,
        // The authenticated user who ran the consult — not a fallback tech.
        techName: userProfile?.displayName || user?.displayName || user?.email || null,
      };
      const docRef = await addDoc(collection(db, 'consult_sessions'), docData);

      setConsultSessions(prev => [...prev, { id: docRef.id, ...docData }]);
      return docRef.id;
    } catch (err) {
      console.error('Error adding consult session:', err);
      throw err;
    }
  }, [user, userProfile]);

  // Add insurance estimate
  const addInsuranceEstimate = useCallback(async (estimateData) => {
    requireWriteAccess();
    try {
      const docRef = await addDoc(collection(db, 'insurance_estimates'), {
        ...estimateData,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        createdBy: user?.email,
        techId: user?.uid,
      });

      const newEstimate = { id: docRef.id, ...estimateData };
      setInsuranceEstimates(prev => [newEstimate, ...prev]);
      return docRef.id;
    } catch (err) {
      console.error('Error adding insurance estimate:', err);
      throw err;
    }
  }, [user]);

  // Update insurance estimate
  const updateInsuranceEstimate = useCallback(async (estimateId, updates) => {
    requireWriteAccess();
    try {
      await setDoc(doc(db, 'insurance_estimates', estimateId), {
        ...updates,
        updatedAt: Timestamp.now(),
      }, { merge: true });

      setInsuranceEstimates(prev => prev.map(e =>
        e.id === estimateId ? { ...e, ...updates } : e
      ));
    } catch (err) {
      console.error('Error updating insurance estimate:', err);
      throw err;
    }
  }, []);

  // Get single insurance estimate
  const getInsuranceEstimate = useCallback((estimateId) => {
    return insuranceEstimates.find(e => e.id === estimateId);
  }, [insuranceEstimates]);

  // Calculate leaderboard data (delegated to pure function)
  const getLeaderboardData = useCallback((lookbackDays = 30) => {
    return computeLeaderboardDataQuery({
      technicians, workOrders, laborEntries, satisfactionScores,
      partsUsage, consultSessions, bankCharges, receipts,
      users, getCardsForTech, lookbackDays,
    });
  }, [technicians, workOrders, laborEntries, satisfactionScores, partsUsage, receipts, consultSessions, bankCharges, users, getCardsForTech]);

  // Get recent WOs a tech has logged labor on
  const getRecentLaborWOsForTech = useCallback((techName, days = 14) => {
    const tech = techName || getCurrentTech()?.name;
    return getRecentLaborWOsForTechQuery(workOrders, laborEntries, tech, days);
  }, [workOrders, laborEntries, getCurrentTech]);

  // ==========================================
  // WO REVIEWS (Flagged WOs) — DISABLED
  // Entire workflow paused. Functions and background analysis commented out.
  // Re-enable when ready to relaunch flagged WO feature.
  // ==========================================

  // const addWoReview = useCallback(async (reviewData) => { ... }, []);
  // const updateWoReview = useCallback(async (reviewId, updates) => { ... }, []);
  // const runBackgroundWOAnalysis = useCallback(async () => { ... }, [...]);
  // useEffect(() => { runBackgroundWOAnalysis ... }, []);

  // ==========================================
  // ADMIN FUNCTIONS - User Management
  // ==========================================

  // Get all users (for admin views)
  const getAllUsers = useCallback(() => {
    return users.sort((a, b) => {
      // Sort by status first (active first), then by name
      if (a.status !== b.status) {
        return a.status === 'active' ? -1 : 1;
      }
      return (a.displayName || a.email || '').localeCompare(b.displayName || b.email || '');
    });
  }, [users]);

  // Get a single user by ID
  const getUser = useCallback((userId) => {
    return users.find(u => u.uid === userId || u.id === userId);
  }, [users]);

  // Create a new user (admin only - for invites)
  const createUser = useCallback(async (userData) => {
    requireWriteAccess();
    const newUser = {
      uid: userData.uid || `invited-${Date.now()}`,
      email: userData.email,
      displayName: userData.displayName || '',
      phone: userData.phone || null,
      photoUrl: null,
      role: userData.role || 'tech',
      status: userData.status || 'invited',
      assignedCards: userData.assignedCards || [],
      createdAt: Timestamp.now(),
      createdBy: userProfile?.uid || 'system',
      updatedAt: Timestamp.now(),
      lastLoginAt: null,
      hireDate: userData.hireDate || null,
      emergencyContact: userData.emergencyContact || null,
    };

    await setDoc(doc(db, 'users', newUser.uid), newUser);
    setUsers(prev => [...prev, newUser]);
    return newUser;
  }, [userProfile]);

  // Update a user (admin only)
  const updateUser = useCallback(async (userId, updates) => {
    requireWriteAccess();
    const updateData = {
      ...updates,
      updatedAt: Timestamp.now(),
    };

    await updateDoc(doc(db, 'users', userId), updateData);
    setUsers(prev => prev.map(u =>
      u.uid === userId ? { ...u, ...updateData } : u
    ));
  }, []);

  // Assign cards to a user (admin only)
  const assignCardsToUser = useCallback(async (userId, cardLast4Array) => {
    requireWriteAccess();
    await updateDoc(doc(db, 'users', userId), {
      assignedCards: cardLast4Array,
      updatedAt: Timestamp.now(),
    });

    setUsers(prev => prev.map(u =>
      u.uid === userId ? { ...u, assignedCards: cardLast4Array } : u
    ));
  }, []);

  // ==========================================
  // ADMIN FUNCTIONS - Card Management
  // ==========================================

  // Get all cards
  const getAllCards = useCallback(() => {
    return cards.sort((a, b) => {
      // Sort by status first (active first), then by holder name
      if (a.status !== b.status) {
        if (a.status === 'active') return -1;
        if (b.status === 'active') return 1;
        if (a.status === 'locked') return -1;
        return 1;
      }
      return (a.holderName || '').localeCompare(b.holderName || '');
    });
  }, [cards]);

  // Get card by last 4 digits
  const getCard = useCallback((cardLast4) => {
    return cards.find(c => c.cardLast4 === cardLast4);
  }, [cards]);

  // Get card with assigned user info
  const getCardWithUser = useCallback((cardLast4) => {
    const card = cards.find(c => c.cardLast4 === cardLast4);
    if (!card) return null;

    // Find user who has this card assigned
    const assignedUser = users.find(u =>
      (u.assignedCards || []).includes(cardLast4)
    );

    return {
      ...card,
      assignedUser: assignedUser ? {
        uid: assignedUser.uid,
        displayName: assignedUser.displayName,
        email: assignedUser.email,
      } : null,
    };
  }, [cards, users]);

  // Update a card (admin only)
  const updateCard = useCallback(async (cardLast4, updates) => {
    requireWriteAccess();
    const updateData = {
      ...updates,
      updatedAt: Timestamp.now(),
    };

    await updateDoc(doc(db, 'cards', cardLast4), updateData);
    setCards(prev => prev.map(c =>
      c.cardLast4 === cardLast4 ? { ...c, ...updateData } : c
    ));
  }, []);

  // Create a new card (admin only)
  const createCard = useCallback(async (cardData) => {
    requireWriteAccess();
    const newCard = {
      cardLast4: cardData.cardLast4,
      holderName: cardData.holderName || null,
      cardName: cardData.cardName || null,
      hasAgreement: cardData.hasAgreement || false,
      phone: cardData.phone || null,
      email: cardData.email || null,
      status: cardData.status || 'active',
      notes: cardData.notes || null,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };

    await setDoc(doc(db, 'cards', newCard.cardLast4), newCard);
    setCards(prev => [...prev, newCard]);
    return newCard;
  }, []);

  // ==========================================
  // INTELLIGENCE LAYER — Knowledge, Predictions, Patterns
  // ==========================================

  // Tribal Knowledge — query functions
  const getKnowledgeForUnit = useCallback((unitId) => {
    return getNotesForUnitQuery(knowledgeNotes, unitId);
  }, [knowledgeNotes]);

  const getKnowledgeForProperty = useCallback((propertyId) => {
    return getNotesForPropertyQuery(knowledgeNotes, propertyId);
  }, [knowledgeNotes]);

  const getKnowledgeForBriefing = useCallback((unitId, propertyId, limit = 5) => {
    return getRelevantNotesForBriefingQuery(knowledgeNotes, unitId, propertyId, limit);
  }, [knowledgeNotes]);

  const searchKnowledge = useCallback((query, propertyId = null) => {
    return searchNotesQuery(knowledgeNotes, query, propertyId);
  }, [knowledgeNotes]);

  const getKnowledgeStats = useCallback((propertyId) => {
    return getPropertyKnowledgeStatsQuery(knowledgeNotes, propertyId, units);
  }, [knowledgeNotes, units]);

  // Tribal Knowledge — CRUD
  const addKnowledgeNote = useCallback(async (noteData) => {
    requireWriteAccess();
    const validation = validateNote(noteData);
    if (!validation.valid) {
      throw new Error(`Invalid note: ${validation.errors.join(', ')}`);
    }

    const note = {
      ...noteData,
      createdAt: new Date().toISOString(),
      createdBy: userProfile?.displayName || user?.displayName || user?.email,
      upvotes: 0,
      flagged: false,
      tags: noteData.tags || [],
    };

    const docRef = await addDoc(collection(db, 'knowledge_notes'), note);
    const newNote = { id: docRef.id, ...note };
    setKnowledgeNotes(prev => [...prev, newNote]);
    return docRef.id;
  }, [user, userProfile]);

  const upvoteKnowledgeNote = useCallback(async (noteId) => {
    const note = knowledgeNotes.find(n => n.id === noteId);
    if (!note) throw new Error('Note not found');

    const newUpvotes = (note.upvotes || 0) + 1;
    await updateDoc(doc(db, 'knowledge_notes', noteId), { upvotes: newUpvotes });
    setKnowledgeNotes(prev => prev.map(n =>
      n.id === noteId ? { ...n, upvotes: newUpvotes } : n
    ));
  }, [knowledgeNotes]);

  const flagKnowledgeNote = useCallback(async (noteId) => {
    requireWriteAccess();
    await updateDoc(doc(db, 'knowledge_notes', noteId), { flagged: true });
    setKnowledgeNotes(prev => prev.map(n =>
      n.id === noteId ? { ...n, flagged: true } : n
    ));
  }, []);

  // Smart Parts Kit — predictions
  const getPredictedParts = useCallback((wo) => {
    return predictPartsForWOQuery(partsUsage, workOrders, wo);
  }, [partsUsage, workOrders]);

  const getIssueTypes = useCallback((description) => {
    return classifyIssueType(description);
  }, []);

  // Predictive Failure Intelligence
  const getRecurringIssues = useCallback((unitId) => {
    return detectRecurringIssuesQuery(workOrders, unitId);
  }, [workOrders]);

  const getCrossPropertyPatterns = useCallback((propertyId) => {
    return findCrossPropertyPatternsQuery(workOrders, propertyId);
  }, [workOrders]);

  const getFailureProbability = useCallback((unitId) => {
    return calculateFailureProbabilityQuery(workOrders, unitId);
  }, [workOrders]);

  const getPropertyHealth = useCallback((propertyId) => {
    return getPropertyHealthScoreQuery(workOrders, propertyId, units);
  }, [workOrders, units]);

  const getSeasonalPatterns = useCallback((propertyId = null) => {
    return analyzeSeasonalPatternsQuery(workOrders, propertyId);
  }, [workOrders]);

  // Repair Confidence Scoring
  const getRepairConfidence = useCallback((wo) => {
    return scoreRepairConfidenceQuery(wo, workOrders, laborEntries, partsUsage);
  }, [workOrders, laborEntries, partsUsage]);

  const getTechReliability = useCallback((techName) => {
    return getTechReliabilityScoreQuery(techName, workOrders, laborEntries, partsUsage);
  }, [workOrders, laborEntries, partsUsage]);

  const getUnitRepairSummary = useCallback((unitId) => {
    return getUnitRepairSummaryQuery(unitId, workOrders, laborEntries, partsUsage);
  }, [workOrders, laborEntries, partsUsage]);

  // Composite intelligence for AI briefings
  const getIntelligenceForBriefing = useCallback((wo) => {
    if (!wo) return null;

    const knowledge = getKnowledgeForBriefing(wo.unitId, wo.propertyId);
    const parts = getPredictedParts(wo);
    const recurring = getRecurringIssues(wo.unitId);
    const failureProb = getFailureProbability(wo.unitId);
    const repairSummary = getUnitRepairSummary(wo.unitId);

    return {
      knowledgeNotes: formatNotesForPrompt(knowledge),
      predictedParts: formatPartsListForPrompt(parts),
      failureIntel: [
        failureProb.score > 0 ? `Failure probability: ${failureProb.score}/100 (${failureProb.level})` : '',
        ...failureProb.factors.map(f => `- ${f.factor}: ${f.detail}`),
        recurring.length > 0 ? `\nRecurring issues: ${recurring.map(r => `${r.description} (${r.occurrences}x)`).join(', ')}` : '',
      ].filter(Boolean).join('\n') || 'No failure intelligence available.',
      repairConfidence: repairSummary.totalClosed > 0
        ? `Avg repair confidence at this unit: ${repairSummary.avgConfidence}/100 (${repairSummary.totalClosed} closed WOs, ${repairSummary.lowConfidenceCount} low-confidence)`
        : 'No prior repair data for this unit.',
      // Raw data for UI display
      _raw: { knowledge, parts, recurring, failureProb, repairSummary },
    };
  }, [getKnowledgeForBriefing, getPredictedParts, getRecurringIssues, getFailureProbability, getUnitRepairSummary]);

  const value = {
    // Raw data
    workOrders,
    laborEntries,
    units,
    properties,
    technicians,
    bankCharges,
    satisfactionScores,
    partsUsage,
    receipts,
    lockChanges,
    inspections,
    consultSessions,
    cards,
    lockCodes,
    unitDetails,
    unitDetailChanges,
    insuranceEstimates,
    knowledgeNotes,
    // woReviews, — disabled (flagged WO workflow paused)
    users,

    // State
    loading,
    refreshing,
    lastRefresh,
    error,
    syncStatus,

    // Access control (from AuthContext)
    canWrite: canWrite || (() => true), // Fallback for safety

    // Functions
    refreshDataFromStorage,
    getCurrentTech,
    setPovTech,
    povTechId,
    getOpenWOsForTech,
    getWOsForUnit,
    getWOsForProperty,
    getLaborForWO,
    getPartsForWO,
    getUnmatchedBankCharges,
    getReceiptsForWO,
    getUnitLockInfo,
    getPropertyInfo,
    getTechByCard,
    getCardsForTech,
    getLockCode,
    getUnitDetails,
    getUnitDetailChanges,
    saveUnitDetails,
    addReceipt,
    addLockChange,
    addInspection,
    updateInspection,
    addConsultSession,
    getLeaderboardData,
    getRecentLaborWOsForTech,
    // addWoReview, — disabled (flagged WO workflow paused)
    // updateWoReview, — disabled (flagged WO workflow paused)
    addInsuranceEstimate,
    updateInsuranceEstimate,
    getInsuranceEstimate,

    // Card assignment (new system)
    getCardsForUser,
    getAvailableCards,
    assignCardToSelf,
    unassignCardFromSelf,

    // Admin: User management
    getAllUsers,
    getUser,
    createUser,
    updateUser,
    assignCardsToUser,

    // Admin: Card management
    getAllCards,
    getCard,
    getCardWithUser,
    updateCard,
    createCard,

    // Intelligence: Tribal Knowledge
    getKnowledgeForUnit,
    getKnowledgeForProperty,
    getKnowledgeForBriefing,
    searchKnowledge,
    getKnowledgeStats,
    addKnowledgeNote,
    upvoteKnowledgeNote,
    flagKnowledgeNote,

    // Intelligence: Smart Parts Kit
    getPredictedParts,
    getIssueTypes,

    // Intelligence: Predictive Failure
    getRecurringIssues,
    getCrossPropertyPatterns,
    getFailureProbability,
    getPropertyHealth,
    getSeasonalPatterns,

    // Intelligence: Repair Confidence
    getRepairConfidence,
    getTechReliability,
    getUnitRepairSummary,

    // Intelligence: Composite briefing
    getIntelligenceForBriefing,
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};

export default DataContext;
